/**
 * Skeleton: soft mint placeholder under the page-wide light sweep. Spec:
 * `brand-redesign-docs/10-SKELETONS.md`.
 *
 * `Skeleton` is one bone (bar, circle, pill, block). `SkeletonLine` is the
 * workhorse for text: a line box exactly as tall as the real line (font size
 * times line height) with a thinner bar centred in it, so the loaded text
 * lands where its placeholder was. Always prefer `SkeletonLine` over a bare
 * bar wherever text will appear.
 *
 * Variants: `text` (small line), `heading` (larger line), `card` (full block),
 * `avatar` (circle). Width / height are overridable via props.
 */

import type { CSSProperties } from "react";

export type SkeletonVariant = "text" | "heading" | "card" | "avatar";

export interface SkeletonProps {
  variant?: SkeletonVariant;
  width?: number | string;
  height?: number | string;
  radius?: string;
  /** `frame`: a quiet surface with no sweep, for panels and cards. */
  tone?: "bone" | "frame";
  /** Render as a block (no inline strut under it). */
  block?: boolean;
  className?: string;
  style?: CSSProperties;
}

const RECIPES: Record<
  SkeletonVariant,
  { width: string | number; height: number; radius: string }
> = {
  text: { width: "100%", height: 12, radius: "6px" },
  heading: { width: "60%", height: 20, radius: "6px" },
  card: { width: "100%", height: 120, radius: "var(--radius-md)" },
  avatar: { width: 36, height: 36, radius: "var(--radius-pill)" },
};

export function Skeleton({
  variant = "text",
  width,
  height,
  radius,
  tone,
  block = false,
  className,
  style,
}: SkeletonProps) {
  const r = RECIPES[variant];
  return (
    <span
      aria-hidden
      className={["skeleton", className].filter(Boolean).join(" ")}
      data-tone={tone === "frame" ? "frame" : undefined}
      style={{
        display: block ? "block" : "inline-block",
        flexShrink: 0,
        width: width ?? r.width,
        height: height ?? r.height,
        borderRadius: radius ?? r.radius,
        ...style,
      }}
    />
  );
}

export interface SkeletonLineProps {
  /** Height of the real line box: a number in px, or any CSS length, e.g.
   *  `"calc(13px * 1.5)"`. Use the real text's font size times line height. */
  lineHeight: number | string;
  /** Bar height. Defaults to about 60% of the line, never under 8px. */
  barHeight?: number | string;
  /** Bar width. Defaults to 70% for one line, 100% for the full lines of a
   *  paragraph. */
  width?: number | string;
  /** Number of lines. The last one is shorter unless `lastWidth` is set. */
  lines?: number;
  lastWidth?: number | string;
  /** Gap between lines, when the real paragraph has one (rare). */
  gap?: number;
  className?: string;
  style?: CSSProperties;
}

function defaultBar(lineHeight: number | string): number | string {
  if (typeof lineHeight === "number") return Math.max(8, Math.round(lineHeight * 0.58));
  return `max(8px, calc(${lineHeight} * 0.58))`;
}

export function SkeletonLine({
  lineHeight,
  barHeight,
  width,
  lines = 1,
  lastWidth,
  gap = 0,
  className,
  style,
}: SkeletonLineProps) {
  const bar = barHeight ?? defaultBar(lineHeight);
  const box = (w: number | string, key?: number) => (
    <span key={key} className="skel-line" style={{ height: lineHeight }}>
      <Skeleton width={w} height={bar} radius="6px" />
    </span>
  );
  if (lines <= 1) {
    return (
      <span aria-hidden className={["skel-line", className].filter(Boolean).join(" ")} style={{ height: lineHeight, ...style }}>
        <Skeleton width={width ?? "70%"} height={bar} radius="6px" />
      </span>
    );
  }
  return (
    <span aria-hidden className={["skel-lines", className].filter(Boolean).join(" ")} style={{ gap, ...style }}>
      {Array.from({ length: lines }).map((_, i) =>
        box(i === lines - 1 ? (lastWidth ?? "55%") : (width ?? "100%"), i),
      )}
    </span>
  );
}
